import React, { useState } from 'react';
import { FaTimes, FaSave, FaSpinner, FaEye, FaEyeSlash, FaCheckCircle, FaTimes as FaTimesIcon } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { API_URL } from '../config';

function ChangePasswordModal({ isOpen, onClose, userId }) {
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [passwordErrors, setPasswordErrors] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false
  });
  const [passwordStrength, setPasswordStrength] = useState(0);

  const validatePassword = (password) => {
    const errors = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[@$!%*?&]/.test(password)
    };
    setPasswordErrors(errors);
    const score = Object.values(errors).filter(Boolean).length;
    setPasswordStrength(score);
    return score === 5;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'new_password') {
      validatePassword(value);
    }
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength === 0) return { text: 'No password', color: '#9CA3AF' };
    if (passwordStrength <= 2) return { text: 'Weak', color: '#EF4444' };
    if (passwordStrength <= 3) return { text: 'Medium', color: '#F59E0B' };
    if (passwordStrength <= 4) return { text: 'Strong', color: '#10B981' };
    return { text: 'Very Strong', color: '#059669' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate
      if (!formData.current_password) {
        toast.error('Please enter your current password');
        setLoading(false);
        return;
      }
      if (formData.new_password.length < 8) {
        toast.error('New password must be at least 8 characters');
        setLoading(false);
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        toast.error('Passwords do not match');
        setLoading(false);
        return;
      }
      if (passwordStrength < 5) {
        toast.error('Please meet all password requirements');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/users/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          current_password: formData.current_password,
          new_password: formData.new_password
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('✅ Password changed successfully!');
        setFormData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        setPasswordStrength(0);
        setPasswordErrors({
          minLength: false,
          hasUppercase: false,
          hasLowercase: false,
          hasNumber: false,
          hasSpecial: false
        });
        onClose();
      } else {
        toast.error(data.error || 'Failed to change password');
      }
    } catch (err) {
      console.error('Error changing password:', err);
      toast.error('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-navy flex items-center gap-2">
            🔒 Change Password
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-navy transition-colors">
            <FaTimes size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  name="current_password"
                  value={formData.current_password}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  placeholder="Enter your current password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showCurrentPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  name="new_password"
                  value={formData.new_password}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green"
                  placeholder="Enter new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              {/* Password Requirements */}
              <div className="mt-3 space-y-1.5">
                <p className="text-xs font-medium text-gray-600">Password Requirements:</p>
                <div className={`flex items-center gap-2 text-xs ${passwordErrors.minLength ? 'text-green-600' : 'text-gray-400'}`}>
                  {passwordErrors.minLength ? <FaCheckCircle className="text-green-500" /> : <FaTimesIcon className="text-gray-300" />}
                  At least 8 characters
                </div>
                <div className={`flex items-center gap-2 text-xs ${passwordErrors.hasUppercase ? 'text-green-600' : 'text-gray-400'}`}>
                  {passwordErrors.hasUppercase ? <FaCheckCircle className="text-green-500" /> : <FaTimesIcon className="text-gray-300" />}
                  At least 1 uppercase letter (A-Z)
                </div>
                <div className={`flex items-center gap-2 text-xs ${passwordErrors.hasLowercase ? 'text-green-600' : 'text-gray-400'}`}>
                  {passwordErrors.hasLowercase ? <FaCheckCircle className="text-green-500" /> : <FaTimesIcon className="text-gray-300" />}
                  At least 1 lowercase letter (a-z)
                </div>
                <div className={`flex items-center gap-2 text-xs ${passwordErrors.hasNumber ? 'text-green-600' : 'text-gray-400'}`}>
                  {passwordErrors.hasNumber ? <FaCheckCircle className="text-green-500" /> : <FaTimesIcon className="text-gray-300" />}
                  At least 1 number (0-9)
                </div>
                <div className={`flex items-center gap-2 text-xs ${passwordErrors.hasSpecial ? 'text-green-600' : 'text-gray-400'}`}>
                  {passwordErrors.hasSpecial ? <FaCheckCircle className="text-green-500" /> : <FaTimesIcon className="text-gray-300" />}
                  At least 1 special character (@$!%*?&)
                </div>
              </div>

              {/* Password Strength Bar */}
              {formData.new_password && (
                <div className="mt-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium text-gray-600">Password Strength</span>
                    <span className="text-xs font-bold" style={{ color: getPasswordStrengthText().color }}>
                      {getPasswordStrengthText().text}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${(passwordStrength / 5) * 100}%`,
                        backgroundColor: getPasswordStrengthText().color
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirm_password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green ${
                    formData.confirm_password && formData.new_password === formData.confirm_password
                      ? 'border-green-500 bg-green-50'
                      : formData.confirm_password && formData.new_password !== formData.confirm_password
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-300'
                  }`}
                  placeholder="Confirm new password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {formData.confirm_password && formData.new_password !== formData.confirm_password && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                  <FaTimesIcon /> Passwords do not match
                </p>
              )}
              {formData.confirm_password && formData.new_password === formData.confirm_password && formData.new_password && (
                <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                  <FaCheckCircle /> Passwords match
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || passwordStrength < 5 || formData.new_password !== formData.confirm_password}
              className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSave size={14} />}
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChangePasswordModal;
